/**
 * UI popups for play GBUX enter/leave — best practices.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Coins, LogOut, Wallet } from "lucide-react";
import { formatPlayGbux } from "@shared/gbux-math";
import {
  enterPlayGameBlurb,
  leaveTableToPlay,
  type PlayGameKind,
} from "@/lib/play-ledger-session";
import { useDappSign } from "@/contexts/dapp-sign-context";

/** When play balance is 0 — fund before first spin/deal/join */
export function FundPlayDialog({
  open,
  onClose,
  playGbux,
  game,
  onFund,
}: {
  open: boolean;
  onClose: () => void;
  playGbux: number;
  game: PlayGameKind;
  onFund: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-950 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-200">
            <Coins className="h-5 w-5" />
            Fund real play GBUX
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm leading-relaxed">
            Your dApp play balance is{" "}
            <strong className="text-amber-200">
              {formatPlayGbux(playGbux)}
            </strong>{" "}
            GBUX (real product money for poker.grudge-studio.com — not fake
            chips). {enterPlayGameBlurb(game)}
          </DialogDescription>
        </DialogHeader>
        <p className="text-xs text-zinc-500">
          Fund via: (1) SPL transfer from Phantom/other wallet → play 1:1, or
          (2) buy @ <strong className="text-amber-200/90">$0.001/GBUX</strong>{" "}
          in SOL/USDT via the AI agent. One 24h play session — no re-sign every
          page.
        </p>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" className="border-zinc-700" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-amber-600 text-black hover:bg-amber-500"
            onClick={() => {
              onFund();
              onClose();
            }}
          >
            <Wallet className="mr-2 h-4 w-4" />
            Open wallet · fund play
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Leave table — chips return to play ledger (no chain sign) */
export function LeaveTableDialog({
  open,
  onClose,
  tableId,
  wallet,
  stackHint,
  onLeft,
}: {
  open: boolean;
  onClose: () => void;
  tableId: string;
  wallet: string;
  stackHint?: number;
  onLeft: (cashedOut: number, balance: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await leaveTableToPlay(tableId, wallet);
      if (!r.success) {
        setErr(r.error || "Leave failed");
        return;
      }
      onLeft(r.cashedOut, r.balance);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-950 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-200">
            <LogOut className="h-5 w-5" />
            Leave table → play ledger
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm leading-relaxed">
            Your table stack returns to{" "}
            <strong className="text-emerald-300">real play GBUX</strong> on the
            dApp ledger (same money you funded). This does{" "}
            <strong className="text-zinc-200">not</strong> require a new
            Phantom signature.
            {stackHint != null && stackHint > 0 && (
              <>
                {" "}
                Est. return:{" "}
                <strong className="text-amber-200">
                  {formatPlayGbux(stackHint)}
                </strong>
                .
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {err && (
          <p className="text-sm text-red-300 border border-red-500/30 rounded-lg px-3 py-2">
            {err}
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            className="border-zinc-700"
            disabled={busy}
            onClick={onClose}
          >
            Stay
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-500"
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="mr-2 h-4 w-4" />
            )}
            Leave &amp; cash to play
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compact session status for header chips */
export function PlaySessionStatusLine({ className }: { className?: string }) {
  const { playSessionActive, playSessionExp, ensurePlaySession } =
    useDappSign();
  const remainingH =
    playSessionExp && playSessionActive
      ? Math.max(0, (playSessionExp - Date.now()) / 3_600_000)
      : 0;

  if (playSessionActive) {
    return (
      <span className={className || "text-[10px] text-emerald-400/90"}>
        Play session · ~{remainingH < 1 ? `${Math.round(remainingH * 60)}m` : `${remainingH.toFixed(1)}h`} left · no re-sign
      </span>
    );
  }
  return (
    <button
      type="button"
      className={
        className ||
        "text-[10px] text-amber-400/90 underline-offset-2 hover:underline"
      }
      onClick={() => void ensurePlaySession()}
    >
      Authorize play (once / 24h)
    </button>
  );
}
